import { useEffect, useState } from 'react';
import { Button, Label } from 'reactstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { PageHeading } from '../../layouts/PageHeading';
import { StatusBadge } from '../../components/StatusBadge';
import { VpSelect } from '../../components/ui/VpSelect';
import { PromptsGrid } from '../../components/editors/PromptsGrid';
import { RetryPolicyForm } from '../../components/editors/RetryPolicyForm';
import { CommandsEditor } from '../../components/editors/CommandsEditor';
import {
  type DialogTemplateInput,
  type RetryPolicy,
  useActiveDialogTemplate,
  useActivateDialogTemplate,
  useCreateDialogTemplate,
  useDialogTemplate,
} from '../../api/dialogTemplates';
import { REQUIRED_FSM_STATES } from '../../constants/fsmStates';
import { VOICE_TTS_OPTIONS } from '../../constants/voiceTtsVoices';

const DEFAULT_RETRY: RetryPolicy = {
  max_retries: 2,
  low_confidence_threshold: 0.65,
  on_low_confidence: 'retry',
  on_max_retries: 'fallback',
};

const STARTER_COMMANDS: Record<string, string[]> = {
  REPEAT: ['ripeti', 'ridimmi'],
  RIPETI_TUTTO: ['ripeti tutto', 'da capo', 'riprendiamo'],
  INFO: ['info', 'dettagli'],
  FORZA: ['forza {n}', 'imposta a {n}'],
  SALTA: ['salta', 'passa al prossimo'],
  LIBRERIA: ['libreria', 'intestazione lista'],
  PAUSA: ['pausa', 'mi fermo'],
  RIPRENDI: ['riprendi', 'continua'],
  CANCEL_MISSION: ['cancella missione', 'annulla missione'],
};

export function DialogTemplateEditor() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const tenantId = search.get('tenant') ? Number(search.get('tenant')) : null;
  const warehouseId = search.get('warehouse') ? Number(search.get('warehouse')) : null;
  const isNew = id === undefined;
  const readOnly = !isNew;

  const existing = useDialogTemplate(isNew ? null : Number(id), token);
  const active = useActiveDialogTemplate(isNew ? warehouseId : null, token);
  const create = useCreateDialogTemplate(token);
  const activate = useActivateDialogTemplate(token);

  const [language, setLanguage] = useState<string>('it');
  const [voiceTts, setVoiceTts] = useState<string | null>('alloy');
  const [prompts, setPrompts] = useState<Record<string, string>>({});
  const [retry, setRetry] = useState<RetryPolicy>(DEFAULT_RETRY);
  const [commands, setCommands] = useState<Record<string, string[]>>(STARTER_COMMANDS);

  useEffect(() => {
    const src = isNew ? active.data : existing.data;
    if (src) {
      setLanguage(src.language);
      setVoiceTts(src.voice_tts);
      setPrompts(src.prompts);
      setRetry(src.retry_policy);
      setCommands(src.commands);
    }
  }, [isNew, active.data, existing.data]);

  const missing = REQUIRED_FSM_STATES.filter((s) => !prompts[s] || prompts[s].trim() === '');
  const allValid = missing.length === 0 && language.length > 0;

  const save = async (thenActivate: boolean) => {
    if (warehouseId === null) return;
    const input: DialogTemplateInput = {
      warehouse_id: warehouseId,
      language,
      voice_tts: voiceTts,
      prompts,
      retry_policy: retry,
      commands,
    };
    try {
      const created = await create.mutateAsync(input);
      if (thenActivate) await activate.mutateAsync(created.id);
      navigate(`/admin/dialog-templates?tenant=${tenantId}&warehouse=${warehouseId}`);
    } catch (e) {
      alert('Errore: ' + String(e));
    }
  };

  return (
    <div>
      <PageHeading
        scope={isNew ? 'Nuova versione' : `Template v${existing.data?.version ?? '?'}`}
        target={isNew ? '' : `#${id}`}
        badge={!isNew && existing.data?.is_active ? <StatusBadge tone="success">attiva</StatusBadge> : null}
        actions={<Button type="button" color="light" size="sm" onClick={() => navigate(-1)}>↩ Annulla</Button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16, maxWidth: 600 }}>
        <label>
          <Label className="mb-1">Lingua</Label>
          <VpSelect ariaLabel="Lingua" isDisabled={readOnly}
            options={[{ value: 'it', label: 'it' }]}
            value={language}
            onChange={(v) => setLanguage(String(v ?? 'it'))} />
        </label>
        <label>
          <Label className="mb-1">Voce TTS</Label>
          <VpSelect ariaLabel="Voce TTS" isDisabled={readOnly} isClearable
            options={VOICE_TTS_OPTIONS.map((v) => ({ value: v.value, label: v.label }))}
            value={voiceTts ?? null}
            onChange={(v) => setVoiceTts(v ? String(v) : null)} />
        </label>
      </div>

      <h3 style={{ fontSize: 14, marginTop: 24 }}>Prompts</h3>
      <PromptsGrid value={prompts} onChange={setPrompts} readOnly={readOnly} />

      <h3 style={{ fontSize: 14, marginTop: 24 }}>Retry policy</h3>
      <RetryPolicyForm value={retry} onChange={setRetry} readOnly={readOnly} />

      <h3 style={{ fontSize: 14, marginTop: 24 }}>Custom commands</h3>
      <CommandsEditor value={commands} onChange={setCommands} readOnly={readOnly} />

      {isNew ? (
        <div style={{
          position: 'sticky', bottom: 0, marginTop: 32, padding: 12, zIndex: 10,
          background: 'var(--vp-surface)', borderTop: '1px solid var(--vp-border)',
          display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
        }}>
          <span style={{ marginRight: 'auto', fontSize: 11, color: 'var(--vp-text-3)' }}>
            {missing.length > 0 ? `Prompt obbligatori mancanti: ${missing.join(', ')}` : 'Pronto per salvare.'}
          </span>
          <Button type="button" color="secondary" outline size="sm" disabled={!allValid || create.isPending} onClick={() => save(false)}>
            Salva
          </Button>
          <Button type="button" color="primary" size="sm" disabled={!allValid || create.isPending} onClick={() => save(true)}>
            Salva e attiva →
          </Button>
        </div>
      ) : existing.data && !existing.data.is_active ? (
        <div
          style={{
            position: 'sticky', bottom: 0, marginTop: 32, padding: 12, zIndex: 10,
            background: 'var(--vp-surface)', borderTop: '1px solid var(--vp-border)',
            display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
          }}
        >
          <span style={{ marginRight: 'auto', fontSize: 11, color: 'var(--vp-text-3)' }}>
            Versione storica · sola lettura. Attiva per sostituire la versione attualmente attiva.
          </span>
          <Button
            type="button"
            color="primary"
            size="sm"
            disabled={activate.isPending}
            onClick={async () => {
              try {
                await activate.mutateAsync(Number(id));
                navigate(`/admin/dialog-templates?tenant=${tenantId}&warehouse=${warehouseId}`);
              } catch (e) {
                alert('Errore: ' + String(e));
              }
            }}
          >
            Attiva questa versione →
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default DialogTemplateEditor;
